<template>
  <div class="xyk-price-component">
    <!-- 表格区域 -->
    <div class="table-container">
      <el-table 
        :data="tableData" 
        border 
        stripe 
        v-loading="internalLoading"
        element-loading-text="数据加载中..." 
        element-loading-spinner="el-icon-loading"
        highlight-current-row 
        @current-change="$emit('current-change', $event)"
        :height="tableHeight" 
        style="width: 100%"
        :key="componentKey"
      >
        
        <el-table-column fixed="left" label="行号" type="index" width="60" align="center"></el-table-column>
        
        <el-table-column 
          v-for="(col, index) in tableColumns" 
          :key="index" 
          :prop="col.prop" 
          :label="col.name" 
          :width="getColumnWidth(col)"
          :show-overflow-tooltip="true" 
          align="center"
        >
        </el-table-column>
        
        <!-- 操作列 -->
        <el-table-column fixed="right" label="操作" width="100" align="center">
          <template slot-scope="scope">
            <el-button type="primary" size="mini" @click="$emit('detail-click', scope.row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
    
    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        background
      >
      </el-pagination>
    </div>
  </div>
</template>

<script>
import { chgDetailsPage } from "@/api/PSI/ChangeDetailsTable.js";

export default {
  name: 'XykPrice',
  props: {
    // 加载状态（从父组件传递）
    loading: {
      type: Boolean,
      default: false
    },
    // 表格高度
    tableHeight: {
      type: Number,
      default: 400
    },
    // 搜索关键字
    searchKeyword: {
      type: String,
      default: ''
    },
    // 状态筛选
    statusFilter: {
      type: String,
      default: '可用'
    },
    // 当前选中的药库ID
    currentDepotId: {
      type: [String, Number],
      default: 1
    }
  },
  data() {
    return {
      // 内部加载状态
      internalLoading: false,
      componentKey: 0,
      // 表格数据
      tableData: [],
      // 分页信息
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      // 表格列配置
      tableColumns: [
        { name: "单据编号", prop: "adjustNum" },
        { name: "创建时间", prop: "createTime" },
        { name: "创建人", prop: "createName" },
        { name: "药品名称", prop: "drugName" },
        { name: "调前成本单价", prop: "agoPurcPric" },
        { name: "调前零售单价", prop: "agoSalePric" },
        { name: "调后成本单价", prop: "afrPurcPric" },
        { name: "调后零售单价", prop: "afrSalePric" },
      ]
    };
  },
  watch: {
    // 监听搜索关键字变化
    searchKeyword: {
      handler(newVal) {
        this.pagination.currentPage = 1;
        this.loadData();
      }
    },
    // 监听状态筛选变化
    statusFilter: {
      handler(newVal) {
        this.pagination.currentPage = 1;
        this.loadData();
      }
    },
    // 监听药库ID变化
    currentDepotId: {
      handler(newVal) {
        console.log('西药库组件 - 药库切换:', newVal);
        this.componentKey += 1;
        this.pagination.currentPage = 1;
        this.loadData();
      },
      immediate: true
    }
  },
  mounted() {
    // 组件挂载时加载数据
    this.loadData();
  },
  methods: {
    // 加载数据
    async loadData() {
      // 通知父组件开始加载数据
      this.$emit('data-loading');
      this.internalLoading = true;
      
      try {
        let res = await chgDetailsPage({
          depotId: this.currentDepotId,
          drugName: this.searchKeyword,
          status: this.getStatusValue(this.statusFilter),
          pageNum: this.pagination.currentPage,
          pageSize: this.pagination.pageSize,
        });
        
        if (res.code === 200) {
          this.tableData = res.data.content || res.data;
          this.pagination.total = res.data.totalSize || this.tableData.length;
        } else {
          this.$message.error(res.message || '获取数据失败');
        }
      } catch (error) {
        console.error('西药库数据加载出错:', error);
        this.$message.error('数据加载失败');
      } finally {
        this.internalLoading = false;
        // 通知父组件数据加载完成
        this.$emit('data-loaded');
      }
    },
    
    // 将状态文字转换为接口参数值
    getStatusValue(statusText) {
      const statusMap = {
        '可用': 'available',
        '禁用': 'disabled',
        '全部': 'all'
      };
      return statusMap[statusText] || 'available';
    },
    
    // 分页大小变化
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.pagination.currentPage = 1;
      this.loadData();
      this.$emit('size-change', val);
    },
    
    // 当前页码变化
    handleCurrentChange(val) {
      this.pagination.currentPage = val;
      this.loadData();
      this.$emit('current-change-page', val);
    },
    
    // 计算列宽
    getColumnWidth(col) {
      const baseWidth = 100;
      const text = col.name;
      let maxWidth = this.calcTextWidth(text);
      
      this.tableData.forEach(row => {
        if (row[col.prop]) {
          const cellWidth = this.calcTextWidth(String(row[col.prop]));
          if (cellWidth > maxWidth) {
            maxWidth = cellWidth;
          }
        }
      });
      
      return Math.max(baseWidth, maxWidth + 20);
    },
    
    // 计算文本宽度
    calcTextWidth(str) {
      if (!str) return 0;
      let length = 0;
      for (const char of String(str)) {
        if ("a" <= char && char <= "z") length += 8;
        else if ("A" <= char && char <= "Z") length += 8;
        else if (Number.isFinite(Number(char))) length += 8;
        else if (char >= "\u4e00" && char <= "\u9fa5") length += 16;
        else length += 8;
      }
      return length;
    }
  }
};
</script>

<style lang="less" scoped>
.xyk-price-component {
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .table-container {
    flex: 1;
    overflow: hidden;
  }
  
  .pagination-container {
    padding: 16px 0;
    border-top: 1px solid #e6e6e6;
    background: #fafbfc;
    display: flex;
    justify-content: flex-end;
  }
}
// 移动端适配
@media screen and (max-width: 768px) {
  .xyk-price-component {
    .table-container {
      overflow-x: auto;
      
      /deep/ .el-table {
        min-width: 800px; // 确保表格在移动端可以横向滚动
      }
    }
    
    .pagination-container {
      padding: 12px 0;
      
      /deep/ .el-pagination {
        .btn-prev,
        .btn-next,
        .el-pager li {
          min-width: 32px;
          height: 32px;
          line-height: 32px;
        }
        
        .el-pagination__jump {
          display: none; // 移动端隐藏跳转输入框
        }
      }
    }
  }
}

// 超小屏幕优化
@media screen and (max-width: 480px) {
  .xyk-price-component {
    .pagination-container {
      /deep/ .el-pagination {
        .el-pagination__sizes {
          display: none; // 超小屏幕隐藏页面大小选择
        }
      }
    }
  }
}
</style>